import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { CloseBig, CloseSmall, Shuffle } from "@tiny/icons";
import { Controller, FormProvider } from "react-hook-form";
import useTrafficSplitForm from "./TrafficSplitForm.hooks";
import { useConfigView } from "../../Config.context";
import {
  getSplitGoalOptions,
  isEmailGoal,
  MAX_SPLIT_BRANCHES,
  MAX_SPLIT_PERCENTAGE,
  MAX_SPLIT_TEST_HOURS,
  MIN_SPLIT_BRANCHES,
  MIN_SPLIT_PERCENTAGE,
  MIN_SPLIT_TEST_HOURS,
  SPLIT_GOAL_OPTIONS,
  SPLIT_WINNER_SELECTION_OPTIONS,
  LIFETIME_SPLIT_HELPER_TEXT,
  LIFETIME_SWITCH_CONFIRMATION,
} from "../../pages/WorkflowsBuilderPage/WorkflowsBuilder/Split/split.helpers";

const toNumberOrEmpty = (value: string) => (value === "" ? "" : Number(value));

const TrafficSplitForm = (props: { id: string }) => {
  const {
    control,
    methods,
    fields,
    formValues,
    errors,
    total,
    addBranch,
    removeBranch,
    canAddBranch,
    canRemoveBranch,
    saveDisabled,
    goBack,
    onSubmit,
    viewMode,
    lifetimeConfirmOpen,
    confirmLifetimeSwitch,
    cancelLifetimeSwitch,
  } = useTrafficSplitForm(props);
  const { config } = useConfigView();
  const isLifetime = formValues.winnerSelection === "LIFETIME";

  // The total has its own line; everything else is listed under the form.
  const otherErrors = errors.filter(
    (error) => !error.startsWith("Percentages must add up")
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} style={{ height: "100%" }}>
        <Box
          sx={{
            py: 4,
            px: 6,
            display: "flex",
            flexDirection: "column",
            width: { xs: "400px", xl: "25vw" },
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Shuffle sx={{ color: "fuchsia.150" }} />
              <Typography variant="h2">Traffic Split</Typography>
            </Box>
            <IconButton sx={{ px: 0 }} onClick={goBack}>
              <CloseBig />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 6,
              mt: 8,
              overflowY: "auto",
            }}
          >
            <Box display="flex" flexDirection="column" gap={3}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="label1" color="grey.100">
                  Paths
                </Typography>
                <Typography
                  variant="label2"
                  color="grey.100"
                  data-testid="split-path-limits"
                  sx={{
                    backgroundColor: "grey.20",
                    borderRadius: 1,
                    px: 2,
                    py: 0.5,
                  }}
                >
                  {fields.length} of {MAX_SPLIT_BRANCHES} paths · min{" "}
                  {MIN_SPLIT_BRANCHES}
                </Typography>
              </Box>
              {fields.map((field, index) => (
                <Box
                  key={field.fieldKey}
                  display="flex"
                  alignItems="flex-start"
                  gap={2}
                >
                  <Controller
                    name={`branches.${index}.name`}
                    control={control}
                    render={({ field: nameField }) => (
                      <TextField
                        size="small"
                        label="Name"
                        value={nameField.value}
                        onChange={nameField.onChange}
                        error={!nameField.value?.trim()}
                        disabled={viewMode}
                        sx={{ flex: 1 }}
                        inputProps={{ "aria-label": `Path ${index + 1} name` }}
                      />
                    )}
                  />
                  <Controller
                    name={`branches.${index}.percentage`}
                    control={control}
                    render={({ field: percentageField }) => {
                      const value = percentageField.value;
                      const invalid =
                        value === "" ||
                        !Number.isInteger(value) ||
                        value < MIN_SPLIT_PERCENTAGE ||
                        value > MAX_SPLIT_PERCENTAGE;
                      return (
                        <TextField
                          size="small"
                          label="Split"
                          type="number"
                          value={value}
                          onChange={(e) =>
                            percentageField.onChange(
                              toNumberOrEmpty(e.target.value)
                            )
                          }
                          error={invalid}
                          disabled={viewMode}
                          sx={{ width: 110 }}
                          inputProps={{
                            min: MIN_SPLIT_PERCENTAGE,
                            max: MAX_SPLIT_PERCENTAGE,
                            step: 1,
                            "aria-label": `Path ${index + 1} percentage`,
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">%</InputAdornment>
                            ),
                          }}
                        />
                      );
                    }}
                  />
                  {canRemoveBranch && !viewMode && (
                    <IconButton
                      aria-label={`Remove path ${index + 1}`}
                      onClick={() => removeBranch(index)}
                      sx={{ mt: 1 }}
                    >
                      <CloseSmall />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  variant="label2"
                  color={total === 100 ? "grey.80" : "error"}
                  data-testid="split-total"
                >
                  Total: {total}%{total !== 100 ? " — must equal 100%" : ""}
                </Typography>
                {!viewMode &&
                  (canAddBranch ? (
                    <Button size="small" onClick={addBranch}>
                      Add path
                    </Button>
                  ) : (
                    <Typography variant="label2" color="grey.80">
                      Maximum {MAX_SPLIT_BRANCHES} paths
                    </Typography>
                  ))}
              </Box>
            </Box>

            <Controller
              name="winnerSelection"
              control={control}
              render={({ field }) => (
                <FormControl>
                  <Typography variant="label1" color="grey.100" mb={1}>
                    Winner selection
                  </Typography>
                  <RadioGroup
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                  >
                    {SPLIT_WINNER_SELECTION_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio size="small" />}
                        disabled={viewMode}
                        sx={{ alignItems: "flex-start", mb: 1 }}
                        label={
                          <Box pt={0.5}>
                            <Typography variant="label1" component="div">
                              {option.label}
                            </Typography>
                            <Typography
                              variant="label2R"
                              color="grey.80"
                              component="div"
                            >
                              {option.description}
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </RadioGroup>
                  {field.value === "LIFETIME" && (
                    <FormHelperText sx={{ color: "grey.80", mx: 0 }}>
                      {LIFETIME_SPLIT_HELPER_TEXT}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="goal"
              control={control}
              render={({ field }) => {
                const goalLabel = isLifetime
                  ? "Track a metric (optional)"
                  : "Goal";
                return (
                  <FormControl fullWidth error={!isLifetime && !field.value}>
                    <InputLabel
                      id="split-goal-label"
                      shrink
                      sx={(theme) => ({
                        backgroundColor: theme.palette.background.paper,
                        px: 2,
                      })}
                    >
                      {goalLabel}
                    </InputLabel>
                    <Select
                      size="small"
                      labelId="split-goal-label"
                      label={goalLabel}
                      displayEmpty
                      disabled={viewMode}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      renderValue={(value) =>
                        SPLIT_GOAL_OPTIONS.find(
                          (option) => option.value === value
                        )?.label || (isLifetime ? "None" : "Select a goal")
                      }
                    >
                      {isLifetime && <MenuItem value="">None</MenuItem>}
                      {getSplitGoalOptions(
                        !!config?.shopifyIntegration,
                        field.value
                      ).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {/* A lifetime split's metric is only tracked, so the
                        backend does not require an email per path for it. */}
                    {!isLifetime && isEmailGoal(field.value || undefined) && (
                      <FormHelperText sx={{ color: "grey.80", mx: 0 }}>
                        Every path needs at least one email of its own before
                        the paths rejoin, or its{" "}
                        {field.value === "OPEN_RATE" ? "open" : "click"} rate
                        cannot be measured.
                      </FormHelperText>
                    )}
                  </FormControl>
                );
              }}
            />

            {formValues.winnerSelection === "AUTOMATIC" && (
              <Controller
                name="testDurationHours"
                control={control}
                render={({ field }) => {
                  const value = field.value;
                  const invalid =
                    value === "" ||
                    !Number.isInteger(value) ||
                    value < MIN_SPLIT_TEST_HOURS ||
                    value > MAX_SPLIT_TEST_HOURS;
                  return (
                    <TextField
                      size="small"
                      type="number"
                      label="Test duration (hours)"
                      required
                      value={value}
                      onChange={(e) =>
                        field.onChange(toNumberOrEmpty(e.target.value))
                      }
                      error={invalid}
                      helperText={`A whole number from ${MIN_SPLIT_TEST_HOURS} to ${MAX_SPLIT_TEST_HOURS}.`}
                      disabled={viewMode}
                      inputProps={{
                        min: MIN_SPLIT_TEST_HOURS,
                        max: MAX_SPLIT_TEST_HOURS,
                        step: 1,
                      }}
                    />
                  );
                }}
              />
            )}

            {!viewMode && otherErrors.length > 0 && (
              <Alert severity="warning" variant="outlined">
                {otherErrors.map((error) => (
                  <Typography key={error} variant="label2" component="div">
                    {error}
                  </Typography>
                ))}
              </Alert>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              p: 2,
            }}
          >
            <Button onClick={goBack}>Cancel</Button>
            {!viewMode && (
              <Button variant="contained" type="submit" disabled={saveDisabled}>
                Save
              </Button>
            )}
          </Box>
        </Box>
      </form>
      <Dialog
        open={lifetimeConfirmOpen}
        onClose={cancelLifetimeSwitch}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h3">Switch to a lifetime split?</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="label1" color="grey.90">
            {LIFETIME_SWITCH_CONFIRMATION}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={cancelLifetimeSwitch}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmLifetimeSwitch}>
            Switch to lifetime
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export default TrafficSplitForm;
